import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listPutawayDirectives,
  claimPutawayDirective,
  executePutawayMove,
  lookupLocationByCode,
} from "@/lib/putaway-directives.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { ScopeBar } from "@/components/scope-bar";
import { HelpButton } from "@/components/kb/help-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { EmptyState } from "@/components/ui/empty-state";
import { LoadingState } from "@/components/ui/loading-state";
import { ArrowRight, MapPin, ScanLine, CheckCircle2, Package, ChevronRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/putaway")({
  component: PutawayPage,
});

type Directive = {
  id: string;
  sku_id: string;
  qty: number;
  priority: number;
  status: string;
  asn_id: string | null;
  asn_line_id: string | null;
  source_location_id: string | null;
  source_location: { id: string; code: string; zone: string | null } | null;
  suggested_location: { id: string; code: string; zone: string | null } | null;
  sku: { id: string; sku_code: string; name: string } | null;
  asn: { id: string; reference: string; status: string; customer_name: string | null } | null;
};

type AsnGroup = {
  asnId: string;
  reference: string;
  customerName: string | null;
  status: string;
  totalLines: number;
  totalQty: number;
  openCount: number;
  inProgressCount: number;
  topPriority: number;
  directives: Directive[];
};

function PutawayPage() {
  const qc = useQueryClient();
  const { tenantId, facilityId } = useScope();

  const listFn = useServerFn(listPutawayDirectives);
  const claimFn = useServerFn(claimPutawayDirective);
  const moveFn = useServerFn(executePutawayMove);
  const lookupFn = useServerFn(lookupLocationByCode);

  const [active, setActive] = useState<Directive | null>(null);
  const [selectedAsnId, setSelectedAsnId] = useState<string | null>(null);
  const [scanCode, setScanCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const scanRef = useRef<HTMLInputElement>(null);

  const { data: directives = [], isLoading } = useQuery<Directive[]>({
    queryKey: ["putaway-directives", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () =>
      runServerFn(
        () => listFn({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
        "Loading putaway queue",
      ) as Promise<Directive[]>,
    refetchInterval: 15_000,
  });

  useEffect(() => {
    if (active && scanRef.current) scanRef.current.focus();
  }, [active]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["putaway-directives", tenantId, facilityId] });

  // Group open/in-progress directives by ASN so the operator drills in per
  // shipment instead of scanning a flat queue of SKU lines.
  const groups: AsnGroup[] = useMemo(() => {
    const map = new Map<string, AsnGroup>();
    for (const d of directives) {
      const key = d.asn_id ?? "__none__";
      const g = map.get(key) ?? {
        asnId: key,
        reference: d.asn?.reference ?? "Ad-hoc / no ASN",
        customerName: d.asn?.customer_name ?? null,
        status: d.asn?.status ?? "—",
        totalLines: 0,
        totalQty: 0,
        openCount: 0,
        inProgressCount: 0,
        topPriority: Number.POSITIVE_INFINITY,
        directives: [],
      };
      g.totalLines += 1;
      g.totalQty += d.qty;
      if (d.status === "open") g.openCount += 1;
      if (d.status === "in_progress") g.inProgressCount += 1;
      g.topPriority = Math.min(g.topPriority, d.priority);
      g.directives.push(d);
      map.set(key, g);
    }
    return [...map.values()].sort((a, b) => a.topPriority - b.topPriority);
  }, [directives]);

  const selectedGroup = selectedAsnId ? groups.find((g) => g.asnId === selectedAsnId) ?? null : null;

  // If the selected ASN drains to zero directives (all completed), return to list.
  useEffect(() => {
    if (selectedAsnId && !isLoading && !groups.find((g) => g.asnId === selectedAsnId)) {
      setSelectedAsnId(null);
      setActive(null);
    }
  }, [selectedAsnId, groups, isLoading]);

  const startDirective = async (d: Directive) => {
    try {
      if (d.status === "open") {
        await runServerFn(() => claimFn({ data: { id: d.id } }), "Claiming directive");
      }
      setActive(d);
      setScanCode(d.suggested_location?.code ?? "");
    } catch (err: any) {
      toast.error(err?.message ?? "Could not claim");
      refresh();
    }
  };

  const submitMove = async () => {
    if (!active || !tenantId || !facilityId) return;
    const code = scanCode.trim();
    if (!code) return toast.error("Scan or enter a destination bin");
    setSubmitting(true);
    try {
      const loc = await runServerFn(
        () => lookupFn({ data: { tenantId, facilityId, code } }),
        "Resolving location",
      );
      if (!loc) {
        toast.error(`No bin matches "${code}"`);
        return;
      }
      const idempotencyKey = `pwd-${active.id}-${loc.id}`;
      await runServerFn(
        () => moveFn({ data: { id: active.id, destinationLocationId: loc.id, idempotencyKey } }),
        "Posting putaway",
      );
      toast.success(`Put ${active.qty} × ${active.sku?.sku_code ?? "SKU"} → ${loc.code}`);
      setActive(null);
      setScanCode("");
      refresh();
    } catch (err: any) {
      toast.error(err?.message ?? "Putaway failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!tenantId || !facilityId) {
    return (
      <div className="p-6">
        <EmptyState title="Pick a Facility" description="Use the scope bar to choose a facility to work in." />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      <div className="border-b bg-card px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {selectedGroup ? (
                <button
                  className="inline-flex items-center gap-1 hover:text-foreground"
                  onClick={() => { setSelectedAsnId(null); setActive(null); }}
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> All ASNs
                </button>
              ) : <span>Inbound</span>}
              {selectedGroup && (
                <>
                  <ChevronRight className="h-3.5 w-3.5" />
                  <span className="font-mono">{selectedGroup.reference}</span>
                </>
              )}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight mt-1">
              {selectedGroup ? `Putaway · ${selectedGroup.reference}` : "Putaway"}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedGroup
                ? `${selectedGroup.totalLines} line${selectedGroup.totalLines === 1 ? "" : "s"} · ${selectedGroup.totalQty} units${selectedGroup.customerName ? ` · ${selectedGroup.customerName}` : ""}`
                : "Pick an ASN to begin putaway. Shipments appear here once received."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ScopeBar />
            <HelpButton moduleKey="receiving" />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-6 space-y-4">
        {!selectedGroup ? (
          <div className="rounded-lg border bg-card overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ASN</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Lines</TableHead>
                  <TableHead className="text-right">Units</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead className="w-14">Pri</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={7}><LoadingState /></TableCell></TableRow>
                ) : groups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <EmptyState
                        title="Putaway Queue Empty"
                        description="ASNs appear here once their receipts are recorded."
                      />
                    </TableCell>
                  </TableRow>
                ) : groups.map((g) => (
                  <TableRow
                    key={g.asnId}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => setSelectedAsnId(g.asnId)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <span className="font-mono font-medium">{g.reference}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{g.customerName ?? "—"}</TableCell>
                    <TableCell className="text-right font-mono">{g.totalLines}</TableCell>
                    <TableCell className="text-right font-mono">{g.totalQty}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-xs">
                        {g.inProgressCount > 0 && (
                          <Badge variant="default">{g.inProgressCount} in progress</Badge>
                        )}
                        <Badge variant="secondary">{g.openCount} open</Badge>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{Number.isFinite(g.topPriority) ? g.topPriority : "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAsnId(g.asnId);
                        }}
                      >
                        Open <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <>
            {active ? (
              <div className="rounded-lg border bg-card p-5 shadow-sm space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Active line</div>
                    <div className="text-lg font-semibold mt-1">
                      {active.sku?.sku_code ?? "SKU"} · {active.sku?.name ?? ""}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
                      <Badge variant="secondary">Qty {active.qty}</Badge>
                      {active.source_location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          {active.source_location.code}
                        </span>
                      )}
                      <ArrowRight className="h-3.5 w-3.5" />
                      <span>Destination bin</span>
                    </div>
                  </div>
                  <Button variant="ghost" onClick={() => { setActive(null); setScanCode(""); }}>
                    Cancel
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pwd-scan">Destination bin (scan or type code)</Label>
                  <div className="flex gap-2">
                    <Input
                      ref={scanRef}
                      id="pwd-scan"
                      value={scanCode}
                      onChange={(e) => setScanCode(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") submitMove(); }}
                      placeholder={active.suggested_location?.code ?? "e.g. A-12-03"}
                      autoComplete="off"
                      className="font-mono text-base"
                    />
                    <Button onClick={submitMove} disabled={submitting}>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      {submitting ? "Posting…" : "Put away"}
                    </Button>
                  </div>
                  {active.suggested_location && (
                    <p className="text-xs text-muted-foreground">
                      Suggested: <span className="font-mono">{active.suggested_location.code}</span>
                      {active.suggested_location.zone && ` (${active.suggested_location.zone})`}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground flex items-center gap-2">
                <ScanLine className="h-4 w-4" />
                Pick a line below to start. Lower priority numbers go first.
              </div>
            )}

            <div className="rounded-lg border bg-card overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-14">Pri</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead className="w-20 text-right">Qty</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>Suggested bin</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedGroup.directives.map((d) => (
                    <TableRow key={d.id} className={active?.id === d.id ? "bg-muted/40" : ""}>
                      <TableCell className="font-mono text-xs">{d.priority}</TableCell>
                      <TableCell>
                        <div className="font-medium text-sm">{d.sku?.sku_code ?? d.sku_id.slice(0, 8)}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-[18rem]">{d.sku?.name ?? ""}</div>
                      </TableCell>
                      <TableCell className="text-right font-mono">{d.qty}</TableCell>
                      <TableCell className="text-sm font-mono">{d.source_location?.code ?? "—"}</TableCell>
                      <TableCell className="text-sm font-mono">{d.suggested_location?.code ?? "—"}</TableCell>
                      <TableCell>
                        <StatusPill tone={statusTone(d.status)}>{d.status}</StatusPill>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" onClick={() => startDirective(d)} disabled={active?.id === d.id}>
                          {d.status === "in_progress" ? "Resume" : "Start"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}